import { useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc' | null;

interface UseSortTableProps<T> {
  initialData: T[];
  defaultSortColumn?: keyof T | null;
  defaultDirection?: SortDirection;
}

interface UseSortTableReturn<T> {
  sortedData: T[];
  sortColumn: keyof T | null;
  sortDirection: SortDirection;
  handleSort: (column: keyof T) => void;
}

export const useTableSort = <T,>({
  initialData,
  defaultSortColumn = null,
  defaultDirection = null,
}: UseSortTableProps<T>): UseSortTableReturn<T> => {
  const [sortColumn, setSortColumn] = useState<keyof T | null>(defaultSortColumn);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultDirection);

  const handleSort = (column: keyof T) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      } else {
        setSortDirection('asc');
        setSortColumn(column);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!sortColumn || !sortDirection || initialData.length === 0) {
      return initialData;
    }

    return [...initialData].sort((a, b) => {
      const valueA = a[sortColumn];
      const valueB = b[sortColumn];

      if (valueA === undefined || valueA === null || valueB === undefined || valueB === null) {
        if (valueA === undefined || valueA === null) {
          return sortDirection === 'asc' ? 1 : -1;
        }
        if (valueB === undefined || valueB === null) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        return 0;
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortDirection === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
      }

      if (
        (valueA instanceof Date || typeof valueA === 'string') &&
        (valueB instanceof Date || typeof valueB === 'string')
      ) {
        const dateA = valueA instanceof Date ? valueA : new Date(valueA);
        const dateB = valueB instanceof Date ? valueB : new Date(valueB);

        if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
          return sortDirection === 'asc'
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime();
        }
      }

      const strA = String(valueA);
      const strB = String(valueB);

      return sortDirection === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
    });
  }, [initialData, sortColumn, sortDirection]);

  return {
    sortedData,
    sortColumn,
    sortDirection,
    handleSort,
  };
};
